import { useAppState } from 'AppProvider';
import { CalendarGrouping } from 'api/calendar/types';

export interface ScheduleViewConfig {
  readonly categoryFilter: boolean;
  /**
   * Collapse threshold: a row with MORE than this many sessions collapses its sessions into a
   * single per-row dropdown; at/below it each session renders as its own inline row.
   * `null` disables collapsing entirely — every session renders as its own row.
   */
  readonly chipsThreshold: number | null;
  readonly groupBy: CalendarGrouping;
  readonly rowDuration: boolean;
  readonly rowImage: boolean;
  readonly rowSpotsRemaining: boolean;
  /** Master toggle for the schedule feature: Browse/Schedule tabs, /search layout,
      button styling, page routing to the ScheduleView variants. */
  readonly showScheduleView: boolean;
  /** When true, the Browse | Schedule tabs are rendered at the top of the schedule and
      browse views. Default `true` — brand admins opt out via the dashboard toggle. */
  readonly showTabs: boolean;
  /** Distance (px) from the top where the sticky calendar header begins sticking on desktop.
   *  Retains its original name because the DB shape is unchanged — no migration required. */
  readonly stickyTopOffset: number;
  /** Distance (px) from the top where the sticky calendar header begins sticking on mobile. */
  readonly stickyTopOffsetMobile: number;
  readonly vibeFilter: boolean;
}

const SCHEDULE_VIEW_DEFAULTS: ScheduleViewConfig = {
  categoryFilter: true,
  chipsThreshold: null,
  groupBy: 'timeOfDay',
  rowDuration: true,
  rowImage: true,
  rowSpotsRemaining: true,
  showScheduleView: false,
  showTabs: false,
  stickyTopOffset: 0,
  stickyTopOffsetMobile: 0,
  vibeFilter: true,
};

/**
 * Returns the brand's Schedule View configuration merged over defaults.
 * All fields in brand_settings.scheduleView are optional — any missing field
 * falls back to its default so existing brands retain current behaviour.
 */
const useScheduleViewConfig = (): ScheduleViewConfig => {
  const { settings } = useAppState(
    (state: Record<string, unknown>) => state.brand,
  );
  const stored = settings?.scheduleView as
    | Partial<ScheduleViewConfig>
    | undefined;

  if (!stored) return SCHEDULE_VIEW_DEFAULTS;

  return {
    showScheduleView:
      stored.showScheduleView ?? SCHEDULE_VIEW_DEFAULTS.showScheduleView,
    showTabs: stored.showTabs ?? SCHEDULE_VIEW_DEFAULTS.showTabs,
    rowImage: stored.rowImage ?? SCHEDULE_VIEW_DEFAULTS.rowImage,
    rowDuration: stored.rowDuration ?? SCHEDULE_VIEW_DEFAULTS.rowDuration,
    rowSpotsRemaining:
      stored.rowSpotsRemaining ?? SCHEDULE_VIEW_DEFAULTS.rowSpotsRemaining,
    groupBy: stored.groupBy ?? SCHEDULE_VIEW_DEFAULTS.groupBy,
    // Preserve an explicit `null` (collapse disabled) instead of falling back to the default —
    // `??` would treat null as "not set" and swap it for 4, defeating the toggle-off state.
    chipsThreshold:
      stored.chipsThreshold === undefined
        ? SCHEDULE_VIEW_DEFAULTS.chipsThreshold
        : stored.chipsThreshold,
    categoryFilter:
      stored.categoryFilter ?? SCHEDULE_VIEW_DEFAULTS.categoryFilter,
    vibeFilter: stored.vibeFilter ?? SCHEDULE_VIEW_DEFAULTS.vibeFilter,
    stickyTopOffset:
      stored.stickyTopOffset ?? SCHEDULE_VIEW_DEFAULTS.stickyTopOffset,
    stickyTopOffsetMobile:
      stored.stickyTopOffsetMobile ??
      SCHEDULE_VIEW_DEFAULTS.stickyTopOffsetMobile,
  };
};

export default useScheduleViewConfig;
